import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Formik } from 'formik';
import type { FormikConfig } from 'formik/dist/types';
import { useSnakeBar, useUserBlockMutation, useUserUnblockMutation, useUserUpdateMutation } from 'app/hooks';
import { useDialog } from 'app/hooks/useDialog';
import { ConfirmDialog } from 'app/components';
import { TranslationKeys } from 'app/translations';
import type { UseQueryRefetch, User } from 'app/types';
import { UserDetailsPanelForm } from './components';
import { useRolesQuery } from '../../../../Hooks';
import type { UserDetailsPanelFormValues } from './UserDetailsPanel.types';
import { StoreActions, useStoreDispatch } from 'app/store';
import type { BlockUserFormValues } from '../../../../Components';
import { BlockUserDialog } from '../../../../Components';

type UserDetailsPanelProps = {
  isLoading?: boolean;
  isLoggedInUser?: boolean;
  user: User | undefined;
  refetchGetUserQuery: UseQueryRefetch;
};

export const UserDetailsPanel = ({
  user,
  refetchGetUserQuery,
  isLoggedInUser = false,
  isLoading = false,
}: UserDetailsPanelProps) => {
  const dispatch = useStoreDispatch();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();
  const { mutate: updateUserMutation, isLoading: isLoadingUserUpdateMutation } = useUserUpdateMutation();
  const { mutate: blockUserMutation } = useUserBlockMutation();
  const { mutate: unblockUserMutation } = useUserUnblockMutation();

  const {
    dialogState: showBlockUserDialog,
    openDialog: openBlockUserDialog,
    closeDialog: closeBlockUserDialog,
  } = useDialog();

  const {
    dialogState: showConfirmUnblockDialog,
    openDialog: openConfirmUnblockDialog,
    closeDialog: closeConfirmUnblockDialog,
  } = useDialog();

  const [isEdit, setIsEdit] = React.useState(false);

  const { data: roles, isLoading: isLoadingRolesQuery } = useRolesQuery();

  const loading = isLoading || isLoadingRolesQuery;

  const formikInitialValues = React.useMemo<FormikConfig<UserDetailsPanelFormValues>['initialValues']>(
    () => ({
      userId: user?.userId || '',
      firstName: user?.personName.firstName || '',
      middleName: user?.personName.middleName || '',
      lastName: user?.personName.lastName || '',
      emailAddress: user?.emailAddress || '',
      ownerId: user?.ownerId || '',
      roleId: user?.roleId || '',
      language: user?.language || 'nl',
      createdAt: user?.createdAt || '',
      internal: user?.internal || false,
    }),
    [
      user?.createdAt,
      user?.emailAddress,
      user?.language,
      user?.ownerId,
      user?.personName.firstName,
      user?.personName.lastName,
      user?.personName.middleName,
      user?.roleId,
      user?.userId,
      user?.internal,
    ],
  );

  const formikOnSubmit = React.useCallback<FormikConfig<UserDetailsPanelFormValues>['onSubmit']>(
    values => {
      const method = 'updateUser';
      const { createdAt, ...otherValues } = values;

      const payload = {
        ...otherValues,
        language: otherValues.language || 'nl',
        accesses: user?.accesses || {},
      };

      updateUserMutation(payload, {
        onSuccess: () => {
          setIsEdit(false);
          refetchGetUserQuery().then(result => {
            if (isLoggedInUser && result.data) {
              dispatch(StoreActions.AppActions.setUser(result.data));
            }
          });
          showSuccessSnakeBar({ method });
        },
        onError: (error: any) => {
          showErrorSnakeBar({ method, message: error?.message });
        },
      });
    },
    [
      dispatch,
      isLoggedInUser,
      refetchGetUserQuery,
      showErrorSnakeBar,
      showSuccessSnakeBar,
      updateUserMutation,
      user?.accesses,
    ],
  );

  const blockUser = React.useCallback<FormikConfig<BlockUserFormValues>['onSubmit']>(
    ({ blockUntil }, { setSubmitting }) => {
      if (user?.userId) {
        const method = 'blockUser';

        blockUserMutation(
          { userId: user.userId, blockUntil: blockUntil ? blockUntil.toISOString() : null },
          {
            onSuccess: () => {
              refetchGetUserQuery();
              closeBlockUserDialog();
              showSuccessSnakeBar({ method });
            },
            onError: (error: any) => {
              setSubmitting(false);
              showErrorSnakeBar({ method, message: error.message });
            },
          },
        );
      }
    },
    [
      blockUserMutation,
      closeBlockUserDialog,
      refetchGetUserQuery,
      showErrorSnakeBar,
      showSuccessSnakeBar,
      user?.userId,
    ],
  );

  const unblockUser = React.useCallback(() => {
    if (user?.userId) {
      const method = 'unblockUser';

      unblockUserMutation(
        { userId: user.userId },
        {
          onSuccess: () => {
            refetchGetUserQuery();
            showSuccessSnakeBar({ method });
          },
          onError: (error: any) => {
            showErrorSnakeBar({ method, message: error.message });
          },
        },
      );
    }
  }, [refetchGetUserQuery, showErrorSnakeBar, showSuccessSnakeBar, unblockUserMutation, user?.userId]);

  return (
    <>
      <Formik<UserDetailsPanelFormValues>
        enableReinitialize
        initialValues={formikInitialValues}
        onSubmit={formikOnSubmit}
      >
        <UserDetailsPanelForm
          isLoading={loading}
          isLoggedInUser={isLoggedInUser}
          disabledActionButtonsWhileLoading={loading || isLoadingUserUpdateMutation}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          user={user}
          roles={roles?.data || []}
          onBlockButtonClick={openBlockUserDialog}
          onUnblockButtonClick={openConfirmUnblockDialog}
        />
      </Formik>

      {showBlockUserDialog && (
        <BlockUserDialog show={showBlockUserDialog} onSubmit={blockUser} onClose={closeBlockUserDialog} />
      )}

      {showConfirmUnblockDialog && (
        <ConfirmDialog
          title={<FormattedMessage id={TranslationKeys.users_unblock_confirmationTitle} />}
          open={showConfirmUnblockDialog}
          onClose={closeConfirmUnblockDialog}
          onConfirm={unblockUser}
        >
          <FormattedMessage id={TranslationKeys.users_unblock_confirmationMessage} />
        </ConfirmDialog>
      )}
    </>
  );
};
